import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Bell, AlertTriangle, AlertOctagon, Megaphone } from 'lucide-react';
import { getActiveNotifications, dismissNotification } from '../../services/notificationService';
import { showError } from '../../utils/notificationHandler';

const CustomerNotificationBanner = () => {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getActiveNotifications();
      setNotifications(data);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    await fetchNotifications();
    setRetrying(false);
  };

  const handleDismiss = async (notificationId) => {
    try {
      setDismissing(true);
      await dismissNotification(notificationId);
      
      // Remove from local state
      const updatedNotifications = notifications.filter(n => n.id !== notificationId);
      setNotifications(updatedNotifications);
      
      // Adjust current index if needed
      if (currentIndex >= updatedNotifications.length && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    } catch (error) {
      showError(error, 'Failed to dismiss notification');
    } finally {
      setDismissing(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < notifications.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const getTypeColors = (type) => {
    switch (type) {
      case 'urgent':
        return {
          bg: 'bg-red-50',
          border: 'border-red-500',
          text: 'text-red-900',
          icon: AlertOctagon,
          iconColor: 'text-red-600'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-500',
          text: 'text-yellow-900',
          icon: AlertTriangle,
          iconColor: 'text-orange-500'
        };
      case 'announcement':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-500',
          text: 'text-purple-900',
          icon: Megaphone,
          iconColor: 'text-purple-600'
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-500',
          text: 'text-blue-900',
          icon: Bell,
          iconColor: 'text-blue-600'
        };
    }
  };

  // Show error state with retry option
  if (error && !loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-md mb-6 p-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <X className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Failed to load notifications. Please try again.
              </p>
              {error.message && (
                <p className="text-xs text-red-600 mt-1">
                  {error.message}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="ml-4 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {retrying ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Retrying...
              </div>
            ) : (
              'Retry'
            )}
          </button>
        </div>
      </motion.div>
    );
  }

  // Don't render if loading or no notifications
  if (loading || notifications.length === 0) {
    return null;
  }

  const currentNotification = notifications[currentIndex];
  const colors = getTypeColors(currentNotification.type);
  const TypeIcon = colors.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`${colors.bg} ${colors.border} border-l-4 rounded-lg shadow-md mb-6 overflow-hidden`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Header */}
              <div className="flex items-center mb-2">
                <TypeIcon className={`h-6 w-6 mr-2 flex-shrink-0 ${colors.iconColor}`} aria-hidden="true" />
                <h3 className={`text-lg font-semibold ${colors.text}`}>
                  {currentNotification.title}
                </h3>
              </div>

              {/* Message Content */}
              <div 
                className="prose prose-sm max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: currentNotification.message }}
              />

              {/* Navigation for multiple notifications */}
              {notifications.length > 1 && (
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePrevious}
                      disabled={currentIndex === 0}
                      className={`p-1 rounded ${
                        currentIndex === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      title="Previous notification"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    <span className="text-xs text-gray-600">
                      {currentIndex + 1} of {notifications.length} notifications
                    </span>

                    <button
                      onClick={handleNext}
                      disabled={currentIndex === notifications.length - 1}
                      className={`p-1 rounded ${
                        currentIndex === notifications.length - 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      title="Next notification"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Dot indicators */}
                  <div className="flex items-center space-x-1">
                    {notifications.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentIndex
                            ? 'bg-gray-600 w-4'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        title={`Go to notification ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Dismiss Button */}
            <button
              onClick={() => handleDismiss(currentNotification.id)}
              disabled={dismissing}
              className="ml-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              title="Dismiss notification"
            >
              {dismissing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
              ) : (
                <X className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CustomerNotificationBanner;
